import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { ListRenderItemInfo } from 'react-native';

import { Carousel } from '@fhs/ui';
import {
  ILoyaltyQuestProgress,
  ILoyaltyQuestUi,
  LoyaltyQuestPrizeType,
  LoyaltyQuestStatus,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';

import {
  ChallengeCard,
  ChallengeCardProps,
  ChallengeCardSkeleton,
  useChallengeCardWidth,
} from '../../components';
import { useChallengesUi } from '../../queries/loyalty.queries';

const renderChallengeCard = ({ item }: ListRenderItemInfo<ChallengeCardProps>) => (
  <ChallengeCard {...item} />
);

function getButtonText(status: LoyaltyQuestStatus) {
  switch (status) {
    case LoyaltyQuestStatus.NOT_ENROLLED:
      return 'Start Challenge';
    case LoyaltyQuestStatus.COMPLETED:
      return undefined;
    default:
      return 'Order Now';
  }
}

function getDescription(challenge: ILoyaltyQuestUi) {
  if (challenge.status === LoyaltyQuestStatus.COMPLETED) {
    if (challenge.prize?.type === LoyaltyQuestPrizeType.BONUS_POINTS) {
      return `${challenge.prize?.points} points were applied to your account 🎉`;
    }
    return 'Your prize is ready to be redeemed 🎉';
  }

  const startDate = new Date(challenge.startDate);
  const endDate = new Date(challenge.endDate);

  return `From ${format(startDate, 'MMMM do')} to ${format(endDate, 'MMMM do')}`;
}

function getBadge(status: LoyaltyQuestStatus, progress?: ILoyaltyQuestProgress | null) {
  switch (status) {
    case LoyaltyQuestStatus.ACTIVATED:
      return 'Challenge Started';

    case LoyaltyQuestStatus.NOT_COMPLETED:
      return `${progress?.current}/${progress?.total} Completed`;

    case LoyaltyQuestStatus.COMPLETED:
      return 'Challenge Completed';

    default:
      return undefined;
  }
}

export function Challenges({ isLoading }: { isLoading: boolean }) {
  const { data } = useChallengesUi();

  const itemWidth = useChallengeCardWidth();

  const challengesCardProps = useMemo(
    () =>
      data?.map<ChallengeCardProps>(challenge => ({
        image: challenge.image,
        title: challenge.name ?? '',
        description: getDescription(challenge),
        onPress: () => {}, // TODO: Implement challenge detail navigation
        buttonText: getButtonText(challenge.status),
        progress: challenge.progress,
        badge: getBadge(challenge.status, challenge.progress),
      })) ?? [],
    [data]
  );

  const renderCarouselLoading = useCallback(() => {
    return <ChallengeCardSkeleton style={{ alignSelf: 'center', marginTop: 16 }} />;
  }, []);

  if (isLoading) {
    return renderCarouselLoading();
  }

  return (
    <Carousel
      renderCarouselLoading={renderCarouselLoading}
      style={{ marginTop: 16 }}
      enableNativeSnap
      data={challengesCardProps}
      itemWidth={itemWidth}
      renderItem={renderChallengeCard}
      scrollEnabled={challengesCardProps.length > 1}
    />
  );
}
